import React, { useState } from 'react';
import { getExperiments } from '@noths/polaris-client-experiments';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { Placement } from '@noths/polaris-client-ribbons-design-system';
import { useRichRelevance } from '@noths/polaris-client-rich-relevance';
import { getOrSetRichRelevanceSessionId } from '@noths/polaris-client-user-configuration';
import { CurrencyCode } from '@noths/polaris-dev-ts-types';
import { GetServerSideProps } from 'next';
import getConfig from 'next/config';

import { ImagePreloadLink } from 'src/components/atoms/ImagePreloadLink';
import { RichTextEditor } from 'src/components/atoms/RichTextEditor';
import { Seo } from 'src/components/atoms/Seo';
import { Carousel } from 'src/components/molecules/Carousel';
import { ImageCard } from 'src/components/molecules/ImageCard';
import { HomepageBanner, HomepageBannerProps } from 'src/components/organisms/HomepageBanner';
import {
  HomepageHeroBanner,
  HomepageHeroBannerProps,
} from 'src/components/organisms/HomepageHeroBanner';
import {
  HomepagePartnerBlock,
  HomepagePartners,
} from 'src/components/organisms/HomepagePartnerBlock';
import { RecentlyViewed } from 'src/components/organisms/RecentlyViewed';
import { UspBanner } from 'src/components/organisms/UspBanner';
import { VideoBlock } from 'src/components/organisms/VideoBlock';
import structuredData from 'src/constants/homepageStructuredData';
import * as contentstackService from 'src/services/contentstack';
import * as styles from 'src/styles/homepage.styles';
import { Row } from 'src/styles/layout';
import * as stylesLayout from 'src/styles/layout.styles';
import { color } from 'src/styles/noths.theme';
import {
  trackCarouselProductClick,
  trackHeroBlockClick,
  trackVideoClick,
} from 'src/tracking/homepageTracking';
import { NextConfig } from 'src/types/NextConfig';
import { PageProps } from 'src/types/PageProps';
import { VideoBlockProps } from 'src/types/Video';
import { fetchPage } from 'src/utils/fetchPage';
import homepageData from 'src/utils/transformers/homepageData';
import { ProductPlacement } from '../types/RecentlyViewed';

const { publicRuntimeConfig } = getConfig() as NextConfig;

interface ImageBlock {
  image: string;
  link: string;
  title: string;
}

interface InfoSection {
  copy: string;
  heading: string;
}

interface Seo {
  canonical: string;
  metaDesc: string;
  metaRobots: string;
  metaTags: string;
  title: string;
}

interface SliderProps {
  heading: string;
  slides: ImageBlock[];
  subheading: string;
}

interface UspBannerProps {
  alt: string;
  image: string;
  subtitle: string;
  title: string;
}

interface Page {
  banner: HomepageBannerProps;
  heroBanner: HomepageHeroBannerProps;
  heroBlocks: ImageBlock[];
  infoSection: InfoSection;
  pageTitle: string;
  partners: HomepagePartners;
  seo: Seo;
  slider: SliderProps;
  uspBanner: UspBannerProps[];
  videoBlock: VideoBlockProps;
}

export interface Props extends PageProps {
  page: Page;
  environment: string;
  isPreview: boolean;
  experiments: string;
  richRelevanceSessionId: string;
  functionalCookiesConsented: boolean;
  currency: CurrencyCode;
}

const testName = 'Homepage';

const Homepage = ({
  page: {
    pageTitle,
    seo,
    uspBanner,
    heroBanner,
    heroBlocks,
    slider,
    videoBlock,
    partners,
    banner,
    infoSection,
  },
  richRelevanceSessionId,
  functionalCookiesConsented,
  currency,
}: Props) => {
  const [richRelevanceData, setRichRelevanceData] = useState<ProductPlacement | undefined>();

  const handleVideoClick = () => {
    const {
      heading,
      video: { youtubeID },
    } = videoBlock;

    trackVideoClick({
      pageTitle,
      sectionNumber: 6,
      lastSectionTitle: heading.toUpperCase() || undefined,
      blockType: 'video block',
      youtubeID,
    });
  };

  useRichRelevance(
    {
      API_KEY: publicRuntimeConfig.RICH_RELEVANCE_API_KEY,
      endpoint: publicRuntimeConfig.RICH_RELEVANCE_URL,
      sessionId: richRelevanceSessionId,
      hasDisabledCookies: !functionalCookiesConsented,
    },

    async (fetchPlacement, clearPlacement) => {
      window.R3_COMMON.addPlacementType(Placement.HomePageRecentlyViewed);
      window.R3_HOME = new window.r3_home();
      await fetchPlacement();
      const placement = window.RR?.data?.JSON?.placements?.[0];

      if (placement) {
        setRichRelevanceData(placement);
      }
      clearPlacement();
    },
  );

  return (
    <div css={styles.wrapper}>
      {heroBanner.bannerImage && (
        <>
          <ImagePreloadLink href={heroBanner.bannerImage.mobile} />
          <ImagePreloadLink href={heroBanner.bannerImage.desktop} desktop />
        </>
      )}
      <Seo {...seo} structuredData={structuredData} />
      <div css={stylesLayout.Container} data-wide={true}>
        <h1 css={styles.pageTitle}>{pageTitle}</h1>

        <section css={stylesLayout.Section}>
          <UspBanner slides={uspBanner} />
        </section>

        <section id="section-1" css={stylesLayout.Section}>
          <HomepageHeroBanner {...heroBanner} testName={testName} />
        </section>

        <section id="section-2" css={stylesLayout.Section}>
          <div css={styles.HeroCtaWrapper}>
            <Row>
              {heroBlocks.map(({ title, image, link }, blockIndex) => (
                // @ts-ignore
                <Row.Column size={{ phone: 6, tablet: 3 }} key={title}>
                  <ImageCard
                    key={title}
                    title={title}
                    image={image}
                    link={link}
                    trackGtmEvent={() =>
                      trackHeroBlockClick({
                        sectionId: 2,
                        blockIndex,
                        ctaText: title,
                        link: link,
                      })
                    }
                  />
                </Row.Column>
              ))}
            </Row>
          </div>
        </section>

        <span
          css={stylesLayout.BorderLine}
          style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
        />

        <section id="section-3" css={stylesLayout.Section} data-testid={`${testName}-SectionThree`}>
          <h2 css={stylesLayout.Heading}>{slider.heading}</h2>
          <div css={styles.CarouselWrapper}>
            <Carousel
              renderSlideComponents={() =>
                slider.slides.map(({ title, image, link }, slideIndex) => (
                  <ImageCard
                    key={title}
                    title={title}
                    image={image}
                    link={link}
                    trackGtmEvent={() => {
                      trackCarouselProductClick({
                        sectionId: 3,
                        slideIndex,
                        ctaText: title,
                        link: link,
                        sectionTitle: slider.heading,
                      });
                    }}
                  />
                ))
              }
            />
          </div>
        </section>

        <span
          css={stylesLayout.BorderLine}
          style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
        />
        <section css={stylesLayout.Section} id="section-5">
          <RecentlyViewed
            currency={currency}
            items={richRelevanceData?.items}
            title={richRelevanceData?.message}
            strategy={richRelevanceData?.strategy}
            placementType={Placement.HomePageRecentlyViewed}
          />
        </section>

        <span
          css={stylesLayout.BorderLine}
          style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
        />

        {videoBlock.shouldDisplay && (
          <>
            <section id="section-6" css={stylesLayout.Section}>
              <VideoBlock
                {...videoBlock}
                headingStyles={stylesLayout.Heading}
                noPadding
                customTrackClick={handleVideoClick}
              />
            </section>
            <span
              css={stylesLayout.BorderLine}
              style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
            />
          </>
        )}

        <section id="section-7" css={stylesLayout.Section} data-double-padding={true}>
          <HomepagePartnerBlock {...partners} sectionId="7" />
        </section>

        <span
          css={stylesLayout.BorderLine}
          style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
        />

        <section id="section-8" css={stylesLayout.Section}>
          <HomepageBanner {...banner} sectionId="8" />
        </section>

        {infoSection.copy && (
          <section css={stylesLayout.Section}>
            <div css={styles.InfoSection}>
              <h2 css={stylesLayout.Heading} data-custom-heading="true">
                {infoSection.heading}
              </h2>
              <RichTextEditor isSmallText copyColor={color.black} html={infoSection.copy} />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// This function gets called at build time
export const getServerSideProps: GetServerSideProps = async (context) => {
  const {
    contentStack: { campaignStack },
  } = publicRuntimeConfig;
  const { req } = context;
  const { rr_session, currency } = req.cookies;
  const isPreview = !!context.preview;
  const urlPath = req.url;
  const data = await fetchPage(campaignStack, 'homepage', '/', homepageData, isPreview);
  const richRelevanceSessionId = getOrSetRichRelevanceSessionId(context.res, rr_session);
  const functionalCookiesConsented = acceptsFunctionalCookies(req.cookies);
  const { NODE_ENV: env } = process.env;
  const experiments = (req && getExperiments(req)) ?? '';
  const [navData, topBannerData] = await contentstackService.fetchData(req, experiments, urlPath);

  return {
    props: {
      page: data,
      environment: env,
      isPreview,
      category: 'Home',
      richRelevanceSessionId,
      functionalCookiesConsented,
      navData,
      topBannerData,
      currency,
    },
  };
};

export default Homepage;
